import {
  parseAiAlt,
  type AiMarkerKind,
  type ParsedAiAlt,
} from "@repo/ai-disclaimer";
import { isAiMarkerKind, isMarkableAsset } from "./aiImagesInventory";
import { storyblokAssetBase } from "./aiIconVariant";
import { isAiBadgePosition, type AiBadgePosition } from "./aiBadgePosition";

/**
 * The `aiImagesConfig` field holding the registry.
 *
 * A textarea of JSON rather than a blok list: the registry is written by the bulk run,
 * not hand-edited, and a blok per asset would make a 500-entry registry unopenable in the
 * editor. Kept on the config story so the icons and the markers travel together — one
 * fetch, one place for an editor to look.
 */
export const MARKED_ASSETS_FIELD = "marked_assets";

/**
 * What the render path knows about one marked asset.
 *
 * `position` is per-asset and optional: most assets want the site-wide default, and only
 * the ones where the default corner covers something — a face, a logo, burnt-in text —
 * carry an override. Absent means "use the default", never "no badge".
 */
export type MarkedAsset = {
  kind: AiMarkerKind;
  position?: AiBadgePosition;
};

/**
 * Storyblok's legacy S3-fronted asset host.
 *
 * An asset record's `filename` may name either
 * `https://s3.amazonaws.com/a.storyblok.com/f/…` or `https://a.storyblok.com/f/…` for
 * the very same file — older uploads carry the first, and the delivery API hands the
 * story field the second. The registry is written from asset records and read against
 * rendered srcs, so without collapsing the two a marked asset simply never matches.
 */
const S3_FRONTED_HOST = /^(?:https?:)?\/\/s3\.amazonaws\.com\//i;

/**
 * The key both sides of the registry agree on, for any spelling of an asset url.
 *
 * Normalises three things the same file arrives with: the transform the loader appended,
 * the legacy S3 host, and a `?focus=`-style query a call site added. All three are
 * cosmetic to identity, and any one of them left in place is a badge that silently fails
 * to render on an asset somebody marked.
 */
export function assetRegistryKey(url: string): string {
  const trimmed = url.trim();

  if (!trimmed) return trimmed;

  const base = storyblokAssetBase(trimmed);
  const withoutQuery = base.split(/[?#]/)[0] ?? base;

  // Scheme first, so a protocol-relative legacy url (`//s3.amazonaws.com/a.storyblok…`)
  // is still recognised as S3-fronted rather than being promoted to https and left
  // pointing at a host no rendered src ever uses.
  return withoutQuery
    .replace(/^http:\/\//i, "https://")
    .replace(/^\/\//, "https://")
    .replace(S3_FRONTED_HOST, "https://");
}

/** Marked assets, keyed by the asset's base url — the form both sides can agree on. */
export type AiMarkerRegistry = Record<string, MarkedAsset>;

/** One row as stored. `id` is not used to resolve; it is what makes drift repairable. */
export type MarkedAssetEntry = {
  id: number;
  url: string;
  kind: AiMarkerKind;
  /** The per-asset corner override, omitted when the asset follows the site default. */
  position?: AiBadgePosition;
};

export type MarkedAssetsDocument = {
  version: 1;
  generatedAt: string;
  assets: MarkedAssetEntry[];
};

export const MARKED_ASSETS_VERSION = 1;

/** A resolved marker, plus the corner the asset asked for (`null` = site default). */
export type ResolvedAiMarker = ParsedAiAlt & {
  position: AiBadgePosition | null;
};

/** The asset fields the registry rebuild reads. Structural, so a route can pass its own type. */
export type MarkableAsset = {
  id: number;
  filename?: string | null;
  alt?: string | null;
  content_type?: string | null;
};

/**
 * Per-asset corner overrides to carry into a rebuild, keyed by asset id.
 *
 * Keyed by id rather than url because that is the identity the admin page writes against
 * and the only one a rebuild can match after a transform has been appended to a url.
 */
export type BadgePositions = Map<number, AiBadgePosition>;

/**
 * Which assets belong in the registry, given a library and the alts a run just wrote.
 *
 * Pure, and separate from the route that publishes it, because this is the decision the
 * whole mechanism turns on and it is the one thing here worth testing directly: the field
 * is REPLACED on every write, so an asset this function omits loses its badge everywhere
 * it appears, and one it wrongly includes gets a badge over an image nobody marked.
 *
 * `writtenAlts` layers this run's writes over the scanned library. That is what makes a
 * revert work: the run hands back the cleaned alt, this reads no marker in it, and the
 * entry drops out — without it the marker comes off the asset and the badge stays on the
 * page.
 *
 * Images and videos. A badge can render over either, and nothing else — a marked PDF
 * could never produce one, and counting it here would put the registry permanently out
 * of step with the `marked` count the config page compares it against.
 *
 * `positions` is carried in rather than re-derived, because the corner an editor chose
 * lives only in the field this call is about to REPLACE. Losing it would silently move
 * every overridden badge back onto whatever it was moved off.
 */
export function selectMarkedAssets(
  assets: MarkableAsset[],
  writtenAlts: Map<number, string> = new Map(),
  positions: BadgePositions = new Map(),
): MarkedAssetEntry[] {
  const entries: MarkedAssetEntry[] = [];

  for (const asset of assets) {
    if (!isMarkableAsset(asset.content_type)) continue;

    const alt = writtenAlts.get(asset.id) ?? asset.alt ?? "";
    const { kind } = parseAiAlt(alt);
    const url = asset.filename;

    if (!kind || !url) continue;

    const position = positions.get(asset.id);

    entries.push(
      position
        ? { id: asset.id, url, kind, position }
        : { id: asset.id, url, kind },
    );
  }

  return entries;
}

/**
 * The registry as it is stored in the field.
 *
 * Serialised through this rather than at the call site so the reader and the writer
 * cannot drift on shape — the one failure this whole mechanism must not have is a
 * registry that writes cleanly and reads as empty.
 */
export function serialiseMarkedAssets(
  assets: MarkedAssetEntry[],
  generatedAt = new Date().toISOString(),
): string {
  const document: MarkedAssetsDocument = {
    version: MARKED_ASSETS_VERSION,
    generatedAt,
    assets: [...assets].sort((a, b) => a.url.localeCompare(b.url)),
  };

  // Sorted and pretty-printed: this field is read by people when a badge does not appear,
  // and a stable order keeps a diff between two runs to the entries that actually moved.
  return JSON.stringify(document, null, 2);
}

/**
 * The lookup the render path uses, from whatever the field happens to contain.
 *
 * Every malformed shape resolves to an empty registry rather than throwing. A registry is
 * an enhancement — without it the badge still renders from the field's own alt — so a
 * hand-edit that breaks the JSON must not take a page down with it.
 */
export function parseMarkedAssets(value: unknown): AiMarkerRegistry {
  const registry: AiMarkerRegistry = {};

  let document: unknown = value;

  if (typeof value === "string") {
    if (!value.trim()) return registry;

    try {
      document = JSON.parse(value);
    } catch {
      return registry;
    }
  }

  if (!document || typeof document !== "object") return registry;

  const { assets } = document as { assets?: unknown };

  if (!Array.isArray(assets)) return registry;

  for (const entry of assets) {
    if (!entry || typeof entry !== "object") continue;

    const { url, kind, position } = entry as {
      url?: unknown;
      kind?: unknown;
      position?: unknown;
    };

    if (typeof url !== "string" || !url.trim()) continue;
    if (!isAiMarkerKind(kind)) continue;

    // An unrecognised corner is dropped rather than rejected with the entry: the kind is
    // what decides whether a badge renders at all, and losing a badge over a typo in a
    // placement field would trade a compliance artifact for a cosmetic preference.
    // Stored keys are normalised on the way in as well as on the way out: a registry
    // written from a transformed url would otherwise never match anything.
    registry[assetRegistryKey(url)] = isAiBadgePosition(position)
      ? { kind, position }
      : { kind };
  }

  return registry;
}

/**
 * The stored rows, ids included, from an `aiImagesConfig` story's content.
 *
 * Distinct from {@link readMarkedAssets}, which is the url-keyed lookup the render path
 * wants. A rebuild needs the ids instead, to carry each asset's chosen corner across a
 * write that replaces the whole field.
 */
export function readMarkedAssetEntries(
  content: Record<string, unknown> | null | undefined,
): MarkedAssetEntry[] {
  const value = content?.[MARKED_ASSETS_FIELD];

  let document: unknown = value;

  if (typeof value === "string") {
    if (!value.trim()) return [];

    try {
      document = JSON.parse(value);
    } catch {
      return [];
    }
  }

  if (!document || typeof document !== "object") return [];

  const { assets } = document as { assets?: unknown };

  if (!Array.isArray(assets)) return [];

  const entries: MarkedAssetEntry[] = [];

  for (const entry of assets) {
    if (!entry || typeof entry !== "object") continue;

    const { id, url, kind, position } = entry as {
      id?: unknown;
      url?: unknown;
      kind?: unknown;
      position?: unknown;
    };

    if (typeof id !== "number" || typeof url !== "string" || !url.trim()) {
      continue;
    }
    if (!isAiMarkerKind(kind)) continue;

    entries.push(
      isAiBadgePosition(position)
        ? { id, url: url.trim(), kind, position }
        : { id, url: url.trim(), kind },
    );
  }

  return entries;
}

/** The corner overrides an editor has set, keyed by asset id, for a registry rebuild. */
export function readBadgePositions(
  content: Record<string, unknown> | null | undefined,
): BadgePositions {
  const positions: BadgePositions = new Map();

  for (const entry of readMarkedAssetEntries(content)) {
    if (entry.position) positions.set(entry.id, entry.position);
  }

  return positions;
}

/** Reads the registry straight off an `aiImagesConfig` story's content. */
export function readMarkedAssets(
  content: Record<string, unknown> | null | undefined,
): AiMarkerRegistry {
  return parseMarkedAssets(content?.[MARKED_ASSETS_FIELD]);
}

/**
 * The marker for an image, from its own alt text or from the registry.
 *
 * The alt wins whenever it carries a marker. Storyblok copies an asset's alt into a story
 * field when the asset is picked and then stops linking the two, so a field marker is a
 * deliberate statement about this placement, while the registry describes the asset
 * everywhere it is used. Explicit beats inherited, and it keeps single-placement
 * labelling possible.
 *
 * The per-asset corner comes back with it, or `null` where the asset has none — the
 * caller then falls back to the site-wide default.
 *
 * `cleanAlt` is passed through untouched on a registry hit: the marker was never in the
 * text, so there is nothing to strip, and rewriting it would put a marker into the DOM.
 */
export function resolveAiMarker(
  src: string | null | undefined,
  alt: string | null | undefined,
  registry?: AiMarkerRegistry | null,
): ResolvedAiMarker {
  const parsed = parseAiAlt(alt);
  // The registry is consulted for the corner even when the alt already answered the
  // kind: the alt says what a placement is, while the corner is a property of the
  // artwork, and an in-field marker must not cost the asset its chosen placement.
  const entry = registry && src ? registry[assetRegistryKey(src)] : undefined;

  if (parsed.isAi) return { ...parsed, position: entry?.position ?? null };

  if (!entry) return { ...parsed, position: null };

  return {
    cleanAlt: parsed.cleanAlt,
    isAi: true,
    kind: entry.kind,
    position: entry.position ?? null,
  };
}
